import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientManagementService } from '@/services/client-management.service';
import { demoClientSummaries, demoClientDetails, demoConversations, demoStats } from '@/data/demo-data';
import type { ClientListFilters } from '@/types/client-management.types';

export const useDemoMode = () => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(null);

  // Check if service is available
  const { data: healthData, error: healthError } = useQuery({
    queryKey: ['service-health'],
    queryFn: () => clientManagementService.checkServiceHealth(),
    retry: 1,
    staleTime: 10000,
  });

  useEffect(() => {
    if (healthData) {
      setServiceAvailable(true);
    } else if (healthError) {
      setServiceAvailable(false);
      // Auto-enable demo mode if service is not available
      setIsDemoMode(true);
    }
  }, [healthData, healthError]);

  const enableDemoMode = () => setIsDemoMode(true);
  const disableDemoMode = () => setIsDemoMode(false);

  // Mock data functions for demo mode
  const getDemoCustomers = (filters: ClientListFilters = {}) => {
    let filteredData = [...demoClientSummaries];

    // Apply filters
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredData = filteredData.filter(client => 
        client.nombre.toLowerCase().includes(searchTerm) ||
        client.apellido?.toLowerCase().includes(searchTerm) ||
        client.correoElectronico?.toLowerCase().includes(searchTerm) ||
        client.numeroTelefono?.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.leadStatus) {
      if (filters.leadStatus === 'Sin estado') {
        // Filter for clients with no leadStatus (null, undefined, or empty string)
        filteredData = filteredData.filter(client => !client.leadStatus);
      } else {
        filteredData = filteredData.filter(client => client.leadStatus === filters.leadStatus);
      }
    }

    if (filters.citaAgendada !== undefined) {
      filteredData = filteredData.filter(client => client.citaAgendada === filters.citaAgendada);
    }

    if (filters.asesorRespondio !== undefined) {
      filteredData = filteredData.filter(client => client.asesorRespondio === filters.asesorRespondio);
    }

    if (filters.idEstado !== undefined) {
      filteredData = filteredData.filter(client => client.idEstado === filters.idEstado);
    }

    return {
      customers: filteredData,
      total: filteredData.length,
      limit: 25,
      offset: 0
    };
  };

  const getDemoCustomerDetails = (customerId: number) => {
    return demoClientDetails[customerId] || null;
  };

  const getDemoConversations = (customerId: number) => {
    return {
      conversations: demoConversations[customerId] || [],
      total: demoConversations[customerId]?.length || 0,
      customerId
    };
  };

  const getDemoStats = () => demoStats;
  
  const getDemoStates = () => [
    { idEstado: 0, estadoDescripcion: "Conversación en proceso" },
    { idEstado: 1, estadoDescripcion: "Agendó cita" },
    { idEstado: 2, estadoDescripcion: "Registrado en CRM" },
    { idEstado: 3, estadoDescripcion: "Conversación abandonada" }
  ];

  return {
    isDemoMode,
    serviceAvailable,
    enableDemoMode,
    disableDemoMode,
    getDemoCustomers,
    getDemoCustomerDetails,
    getDemoConversations,
    getDemoStats,
    getDemoStates
  };
};